import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import type { UseFormReturn } from "react-hook-form"

import { usePaymentTermsContext } from "../contexts/PaymentTermsContext"
import type { PaymentTermsFormValues } from "../schemas/paymentTermsFormSchema"

import { DefaultExpenseAccountSelector } from "./DefaultExpenseAccountSelector"
import { SensitiveAccountField } from "./SensitiveAccountField"

import { CurrencyInput } from "@/components/CurrencyInput"
import { FormField } from "@/components/Forms"
import { type CompanyAccountingTerm, CompanyAccountingType } from "@/graphql/codegen/graphql"
import { PaymentTermsSelector } from "@/screens/Companies/edit/components/accounting/PaymentTermsSelector"

interface PaymentTermsFormProps {
  onSubmit: () => void
  methods: UseFormReturn<PaymentTermsFormValues>
  data?: CompanyAccountingTerm[]
}

export const PaymentTermsForm: FC<PaymentTermsFormProps> = ({ onSubmit, methods, data }) => {
  const currentVendorPaymentTerm = data?.find((term) => term.companyType === CompanyAccountingType.Vendor)
  const currentCustomerPaymentTerm = data?.find((term) => term.companyType === CompanyAccountingType.Customer)
  const { isCompanyVendor, isCompanyCustomer } = usePaymentTermsContext()

  return (
    <form onSubmit={onSubmit}>
      <Box className='space-y-6'>
        {isCompanyVendor && (
          <Box>
            <Typography className='mb-6 text-base font-bold text-gray-800'>
              Company Payment Terms and Credit Limit
            </Typography>
            <Box className='space-y-4'>
              <PaymentTermsSelector
                fieldName='paymentTermIdVendor'
                label='Vendor Payment Terms'
                methods={methods}
                currentAccountingTerm={currentVendorPaymentTerm}
              />

              <FormField
                name='creditLimitVendor'
                label='Vendor Credit Limit'
                render={({ field, fieldState }) => (
                  <CurrencyInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder='0.00'
                  />
                )}
              />

              <SensitiveAccountField />

              <DefaultExpenseAccountSelector
                control={methods.control}
                name='defaultExpenseAccountId'
                label='Default Expense Account for Invoices'
              />
            </Box>
          </Box>
        )}

        {isCompanyCustomer && (
          <Box>
            <Box className='space-y-4'>
              <PaymentTermsSelector
                fieldName='paymentTermIdCustomer'
                label='Customer Payment Terms'
                methods={methods}
                currentAccountingTerm={currentCustomerPaymentTerm}
              />

              <FormField
                name='creditLimitCustomer'
                label='Customer Credit Limit'
                render={({ field, fieldState }) => (
                  <CurrencyInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder='0.00'
                  />
                )}
              />
            </Box>
          </Box>
        )}
      </Box>
    </form>
  )
}
